// Fetch wrapper. Authenticated calls attach the Cognito access token and retry
// once after a token refresh on a 401. Public calls (upload portal) skip auth.
// All requests are same-origin against config.apiBase ('/api'); CloudFront
// injects the X-Origin-Verify header, so the browser never sends it.
import { config } from '../config';
import { ensureAccessToken, refreshTokens } from '../auth/auth';
import { getAccessToken, clearTokens } from '../auth/tokens';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

// Raised when the session cannot be recovered; callers should send the user to
// sign in.
export class UnauthorizedError extends ApiError {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean; // default true
  signal?: AbortSignal;
}

async function parseError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (data && typeof data.error === 'string') return data.error;
  } catch {
    /* fall through */
  }
  return `Request failed (${res.status})`;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal } = opts;
  const url = `${config.apiBase}${path}`;

  const build = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth && token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  };

  let token: string | null = null;
  if (auth) {
    token = await ensureAccessToken();
    if (!token) throw new UnauthorizedError();
  }

  let res = await build(token);

  // The shared Lambda authorizer denies an expired/invalid access token with 403
  // (API Gateway surfaces an authorizer denial as 403, not 401). Our own handlers never
  // return 403, so treat both 401 and 403 as an auth-recovery signal: refresh once and
  // retry, then route to sign-in if it still fails.
  const isAuthFailure = (s: number) => s === 401 || s === 403;
  if (isAuthFailure(res.status) && auth) {
    const refreshed = await refreshTokens();
    if (!refreshed) {
      clearTokens();
      throw new UnauthorizedError();
    }
    res = await build(getAccessToken());
    if (isAuthFailure(res.status)) {
      clearTokens();
      throw new UnauthorizedError();
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, await parseError(res));
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
